import React, { useState, useEffect } from 'react';
import { Eye, TrendingUp, DollarSign, Star, BarChart3 } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const SummaryCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    sky: 'bg-sky-50 text-sky-600',
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  );
};

const ConversionBar = ({ rate }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 bg-gray-100 rounded-full h-1.5">
      <div
        className="bg-gradient-to-r from-violet-500 to-fuchsia-500 h-1.5 rounded-full"
        style={{ width: `${Math.min(100, rate)}%` }}
      />
    </div>
    <span className="text-xs text-gray-500 w-12 text-right">{rate}%</span>
  </div>
);

const CreatorAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    fetch(`${API_URL}/api/creator/analytics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const summary = data?.summary || {};
  const items = data?.items || [];

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">Analytics</h1>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <SummaryCard icon={Eye} label="Total views" value={(summary.total_views || 0).toLocaleString()} color="sky" />
            <SummaryCard icon={TrendingUp} label="Total sales" value={summary.total_sales || 0} color="violet" />
            <SummaryCard icon={DollarSign} label="Total revenue" value={`$${summary.total_revenue_dollars || '0.00'}`} color="emerald" />
            <SummaryCard icon={BarChart3} label="Overall conversion" value={`${summary.overall_conversion_rate || 0}%`} color="amber" />
          </div>

          {/* Per-template table */}
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
              <BarChart3 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No template data yet</p>
              <p className="text-xs text-gray-400 mt-1">Publish templates and start collecting analytics.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <p className="font-semibold text-gray-900">Per-template breakdown</p>
              </div>

              {/* Table header */}
              <div className="hidden md:grid grid-cols-7 gap-4 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <div className="col-span-2">Template</div>
                <div className="text-right">Views</div>
                <div className="text-right">Sales</div>
                <div className="text-right">Revenue</div>
                <div className="text-right">Rating</div>
                <div>Conversion</div>
              </div>

              <div className="divide-y divide-gray-50">
                {items.map(item => (
                  <div key={item.template_id} className="grid grid-cols-1 md:grid-cols-7 gap-2 md:gap-4 px-5 py-4 items-center">
                    <div className="col-span-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-400 capitalize">{item.marketplace_status}</p>
                    </div>
                    <div className="text-sm text-gray-700 md:text-right flex items-center gap-1.5 md:block">
                      <Eye className="w-3.5 h-3.5 text-gray-400 md:hidden" />
                      {item.views_count.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-700 md:text-right flex items-center gap-1.5 md:block">
                      <TrendingUp className="w-3.5 h-3.5 text-gray-400 md:hidden" />
                      {item.purchases_count}
                    </div>
                    <div className="text-sm font-semibold text-emerald-600 md:text-right flex items-center gap-1.5 md:block">
                      <DollarSign className="w-3.5 h-3.5 text-gray-400 md:hidden" />
                      ${item.revenue_dollars}
                    </div>
                    <div className="flex items-center gap-1 md:justify-end">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      <span className="text-sm text-gray-700">{item.average_rating}</span>
                      <span className="text-xs text-gray-400">({item.review_count})</span>
                    </div>
                    <div>
                      <ConversionBar rate={item.conversion_rate} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </CreatorLayout>
  );
};

export default CreatorAnalytics;
